import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import axiosInstance from '../api/axiosInstance';
import getCroppedImg from '../utils/cropImageHelper';
import '../styles/Auth.css';
import "../styles/editProfile.css";

function ProfileUpload() {
  const navigate = useNavigate();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Handle file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setError('');
      setZoom(1);
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload the cropped image to Cloudflare R2 using presigned URL (like flashes flow)
  const handleUpload = async () => {
    if (!imageSrc || !croppedAreaPixels || isUploading) return;

    try {
      setIsUploading(true);
      setError('');

      // 1) Convert the cropped area to a blob
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

      // 2) Ask server for a presigned upload URL in the trippin_bananas bucket under users/
      const contentType = croppedBlob.type || 'image/jpeg';
      const genResp = await axiosInstance.post('/profile/generate-upload-url', {
        contentType,
      });
      const { uploadUrl, fileName } = genResp.data || {};
      if (!fileName) throw new Error('No fileName returned by server');

      // 3) Upload directly to R2 via PUT (no auth header needed)
      if (uploadUrl) {
        const putResp = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
          },
          body: croppedBlob,
        });
        if (!putResp.ok) {
          const txt = await putResp.text();
          throw new Error(`R2 upload failed: ${putResp.status} ${txt}`);
        }
      }

      // 4) Save the object key to DB so profile points to it
      await axiosInstance.post('/profile/save-profile-key', { fileName });

      // 5) Navigate away
      navigate('/home');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload profile photo.');
    } finally {
      setIsUploading(false);
    }
  };

  // Skip uploading
  const handleSkip = () => {
    navigate(-1);
  };

  return (
    <div className="auth-container profile-upload-page">
      <div className="auth-card profile-upload-card">
        <h1>Profile Photo</h1>
        <p className="profile-upload-subtitle">
          First impressions travel fast. Pick a photo people will remember.
        </p>

        {error && <p className="auth-error profile-upload-error">{error}</p>}

        {!imageSrc && (
          <label className="file-input-container" htmlFor="profile-upload-input">
            <span className="file-input-title">Choose a photo</span>
            <span className="file-input-copy">JPG, PNG or HEIC supported</span>
            <input
              id="profile-upload-input"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </label>
        )}

        {imageSrc && (
          <>
            <div className="cropper-container">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="profile-upload-controls">
              <label className="profile-upload-zoom-label" htmlFor="profile-upload-zoom">
                Zoom
              </label>
              <input
                id="profile-upload-zoom"
                className="profile-upload-zoom"
                type="range"
                min="1"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>

            <label className="profile-upload-link" htmlFor="profile-upload-input-replace">
              Choose another photo
              <input
                id="profile-upload-input-replace"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </label>
          </>
        )}

        <div className="button-group profile-upload-actions">
          <button
            className="auth-button profile-upload-primary"
            onClick={handleUpload}
            disabled={!imageSrc || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload Photo'}
          </button>
          <button className="profile-upload-secondary" onClick={handleSkip} disabled={isUploading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfileUpload;
